import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useSharedValidations } from '../../../../hooks/useSharedValidations';
import { zodResolver } from '@hookform/resolvers/zod';
import { message, Col, Row } from 'antd';
import Button from '../../../atoms/Button';
import FormField from '../../../FormField/FormField';
import {
  useAddAwardMutation,
  useUploadAwardDocsMutation,
  useDeleteAwardDocMutation,
} from '../../../../store/services/sellerProfileCreationService';

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear; year >= 1900; year--) {
    years.push({ label: String(year), value: String(year) });
  }
  return years;
};

export default function AddAwardForm({
  profileId,
  initData,
  onSuccess,
}: {
  profileId: string;
  initData?: any;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();

  const [addAward, { isLoading }] = useAddAwardMutation();
  const [uploadDocs] = useUploadAwardDocsMutation();
  const [deleteAwardDoc] = useDeleteAwardDocMutation();

  const { createDescriptionSchemaFun, zodRequiredStringValidate, documentsValidationFun } = useSharedValidations();

  const schema = z.object({
    awardName: zodRequiredStringValidate.min(2, {
      message: t('components.validations.minLength', { value: 2 }),
    }),
    awardYear: zodRequiredStringValidate,
    description: createDescriptionSchemaFun(20, 1000, true),
    attachments: z.preprocess(
      (files) => {
        if (!Array.isArray(files)) return files;
        return files.map((file: any) => {
          if (!file?.originFileObj) {
            return {
              ...file,
              type: 'image/jpeg',
              size: file.size !== undefined ? file.size : file.blobSize !== undefined ? file.blobSize : 0,
            };
          }
          return file;
        });
      },
      documentsValidationFun(
        0,
        3,
        [
          'image/jpeg',
          'image/png',
          'image/jpg',
          'image/gif',
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ],
        5,
        false
      )
    ),
  });

  type fieldsTypes = z.infer<typeof schema>;

  const methods = useForm<fieldsTypes>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {
      awardName: '',
      awardYear: '',
      description: '',
      attachments: [] as any[],
    },
  });

  const { reset } = methods;

  useEffect(() => {
    if (initData) {
      reset({
        awardName: initData.awardName || '',
        awardYear: initData.awardYear ? String(initData.awardYear) : '',
        description: initData.description || '',
        attachments: initData.attachments || [],
      });
    }
  }, [initData, reset]);

  const onSubmit = async (data: fieldsTypes) => {
    try {
      // 1. Upload new files
      const newFiles = (data.attachments || []).filter((f: any) => !f.id && f.originFileObj);
      let newAttachments: { id: string }[] = [];
      
      if (newFiles.length > 0) {
        const formData = new FormData();
        newFiles.forEach((f: any) => {
          formData.append('files', f.originFileObj);
        });
        const uploadRes = await uploadDocs(formData).unwrap();
        newAttachments = (uploadRes.payload || []).map((doc: any) => ({ id: String(doc.id) }));
      }

      // 2. Identify deleted files and delete them
      const initAttachmentIds = (initData?.attachments || []).map((a: any) => String(a.id));
      const currentAttachmentIds = (data.attachments || []).filter((a: any) => a.id).map((a: any) => String(a.id));
      const deletedIds = initAttachmentIds.filter((id: string) => !currentAttachmentIds.includes(id));
      
      if (deletedIds.length > 0) {
        await Promise.all(deletedIds.map((id: string) => deleteAwardDoc({ documentId: id }).unwrap()));
      }

      // 3. Prepare payload and submit
      const existingAttachments = currentAttachmentIds.map((id: string) => ({ id }));
      const payload = {
        ...(initData?.id ? { id: initData.id } : {}),
        awardName: data.awardName,
        awardYear: Number(data.awardYear),
        ...(data.description?.trim() ? { description: data.description.trim() } : {}),
        attachments: [...existingAttachments, ...newAttachments],
      };

      await addAward({ profileId, data: payload as any }).unwrap();
      onSuccess();
    } catch (err) {
      message.error(t('components.somethingWentWrong'));
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} data-testid="profile-creation-add-award-form">
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <FormField
              type="text"
              id="awardName"
              name="awardName"
              label={t('components.award_name')}
              placeholder={t('components.award_name')}
            />
          </Col>
          <Col span={24}>
            <FormField
              showSearch
              type="select"
              name="awardYear"
              id="awardYear"
              label={t('components.award_year')}
              placeholder={t('components.select_year')}
              options={generateYearOptions()}
            />
          </Col>
          <Col span={24}>
            <FormField
              type="textarea"
              id="description"
              name="description"
              label={t('components.award_description')}
              placeholder={t('components.award_description')}
              maxLength={1000}
              optional={true}
              helperText={t('components.textareaMinAndMaxLength', { min: 20, max: 1000 })}
              className="!h-40"
            />
          </Col>
          <Col span={24} className='mt-2'>
            <FormField
              type="file"
              multiple
              id="attachments"
              name="attachments"
              label={t('components.attachments')}
              maxCount={3}
              allowedTypes={['image/jpeg', 'image/png', 'application/pdf', 'application/msword']}
              maxFileSize={5}
              uploadHints={[
                `${t('components.acceptedFilesType')}: .jpg, .png, .pdf, .doc`,
                t('components.maxFilesHint', { count: 3, size: '5MB' }),
              ]}
            />
          </Col>
        </Row>
        <div className="flex flex-row-reverse mt-10">
          <Button type="submit" disabled={isLoading}>
            {initData?.id ? t('components.edit') : t('components.add')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
